import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import type {
  EditableGridColumn,
  EditableGridEditorState,
  EditableGridRowPatchResult,
  EditableGridSuggestOption,
  EditableGridUpdateRowResult,
} from './types';

/** ID для phantom-строки (локальная, ещё не сохранена в БД). */
export const PHANTOM_ROW_ID = -1;

export type UseEditableGridControllerOptions<
  Row,
  ApiPatch extends Record<string, unknown>,
  CreatePayload,
  ColumnKey extends string,
> = {
  columns: EditableGridColumn<Row, ApiPatch, ColumnKey>[];
  getRowId: (row: Row) => number | null | undefined;
  loadRows: () => Promise<Row[]>;
  createRow: (payload: CreatePayload) => Promise<Row>;
  updateRow: (rowId: number, patch: ApiPatch) => Promise<EditableGridUpdateRowResult<Row>>;
  deleteRow: (rowId: number) => Promise<void>;
  defaults: CreatePayload;
  mapCreatedRow?: (created: Row) => Row;
  onError?: (error: unknown) => void;
};

type StartCellRowTarget = number | 'append';

type ActiveCell<ColumnKey extends string> = {
  rowId: number;
  columnKey: ColumnKey;
};

export function useEditableGridController<
  Row,
  ApiPatch extends Record<string, unknown>,
  CreatePayload,
  ColumnKey extends string,
>({
  columns,
  getRowId,
  loadRows,
  createRow,
  updateRow,
  deleteRow,
  defaults,
  mapCreatedRow,
  onError,
}: UseEditableGridControllerOptions<Row, ApiPatch, CreatePayload, ColumnKey>) {
  const handleError = useCallback(
    (error: unknown) => {
      if (onError) {
        onError(error);
        return;
      }
      alert(error instanceof Error ? error.message : String(error));
    },
    [onError]
  );
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingRowIds, setSavingRowIds] = useState<Set<number>>(new Set());
  const [creatingRow, setCreatingRow] = useState(false);
  const creatingRowRef = useRef(false);

  const [activeCell, setActiveCell] = useState<ActiveCell<ColumnKey> | null>(null);
  const [justSavedCell, setJustSavedCell] = useState<ActiveCell<ColumnKey> | null>(null);
  const [editor, setEditor] = useState<EditableGridEditorState>({ value: '' });
  const [suggestSearchEnabled, setSuggestSearchEnabled] = useState(false);

  const [suggestions, setSuggestions] = useState<EditableGridSuggestOption[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);

  /** Phantom row — локальная строка, ещё не сохранённая в БД. */
  const [phantomRow, setPhantomRow] = useState<Row | null>(null);
  const phantomRowRef = useRef<Row | null>(null);

  useEffect(() => {
    phantomRowRef.current = phantomRow;
  }, [phantomRow]);

  /** Авто-сброс эффекта «только что сохранено» через 350ms. */
  useEffect(() => {
    if (!justSavedCell) return;
    const t = setTimeout(() => setJustSavedCell(null), 350);
    return () => clearTimeout(t);
  }, [justSavedCell]);

  const columnMap = useMemo(() => {
    const map = new Map<ColumnKey, EditableGridColumn<Row, ApiPatch, ColumnKey>>();
    columns.forEach(column => map.set(column.key, column));
    return map;
  }, [columns]);

  const activeColumn = useMemo(() => {
    if (!activeCell) return null;
    return columnMap.get(activeCell.columnKey) ?? null;
  }, [activeCell, columnMap]);

  const debouncedMs = activeColumn?.suggest?.debounceMs ?? 250;
  const [debouncedSuggestQuery] = useDebouncedValue(
    activeColumn?.suggest ? editor.value : '',
    debouncedMs
  );

  const getNextColumn = useCallback(
    (currentKey: ColumnKey): ColumnKey | null => {
      const index = columns.findIndex(column => column.key === currentKey);
      if (index === -1) return null;
      return columns[index + 1]?.key ?? null;
    },
    [columns]
  );

  const resetEditor = useCallback(() => {
    setActiveCell(null);
    setEditor({ value: '' });
    setSuggestSearchEnabled(false);
    setSuggestions([]);
    setSuggestLoading(false);
  }, []);

  /** Удаление phantom-строки из rows. */
  const cleanupPhantom = useCallback(() => {
    if (phantomRowRef.current != null) {
      setRows(prev => prev.filter(r => getRowId(r) !== PHANTOM_ROW_ID));
      setPhantomRow(null);
    }
  }, [getRowId]);

  /** Отмена редактирования с очисткой phantom (если активна phantom-строка). */
  const cancelActiveCellWithCleanup = useCallback(() => {
    if (phantomRowRef.current != null) {
      cleanupPhantom();
    }
    resetEditor();
  }, [cleanupPhantom, resetEditor]);

  const getRowById = useCallback(
    (rowId: number) => {
      return rows.find(row => getRowId(row) === rowId) ?? null;
    },
    [getRowId, rows]
  );

  const setRowSaving = useCallback((rowId: number, isSaving: boolean) => {
    setSavingRowIds(prev => {
      const next = new Set(prev);
      if (isSaving) {
        next.add(rowId);
      } else {
        next.delete(rowId);
      }
      return next;
    });
  }, []);

  const isRowSaving = useCallback((rowId: number) => savingRowIds.has(rowId), [savingRowIds]);

  const loadRowsState = useCallback(async () => {
    setLoading(true);
    setPhantomRow(null);
    try {
      const loaded = await loadRows();
      setRows(loaded ?? []);
    } finally {
      setLoading(false);
    }
  }, [loadRows]);

  useEffect(() => {
    void loadRowsState();
  }, [loadRowsState]);

  useEffect(() => {
    if (!activeColumn?.suggest) {
      setSuggestions([]);
      setSuggestLoading(false);
      return;
    }

    if (!suggestSearchEnabled) {
      setSuggestions([]);
      setSuggestLoading(false);
      return;
    }

    const minChars = activeColumn.suggest.minChars ?? 2;
    const query = debouncedSuggestQuery.trim();

    if (query.length < minChars) {
      setSuggestions([]);
      setSuggestLoading(false);
      return;
    }

    let cancelled = false;

    const runSearch = async () => {
      setSuggestLoading(true);
      try {
        const items = await activeColumn.suggest?.search(query);
        if (cancelled) return;
        setSuggestions(items ?? []);
      } finally {
        if (!cancelled) {
          setSuggestLoading(false);
        }
      }
    };

    void runSearch();
    return () => {
      cancelled = true;
    };
  }, [activeColumn, debouncedSuggestQuery, suggestSearchEnabled]);

  const startCellEdit = useCallback(
    (rowTarget: StartCellRowTarget, columnKey: ColumnKey) => {
      if (rowTarget === 'append') {
        const targetColumn = columnMap.get(columnKey) ?? columns[0];
        if (!targetColumn) return;

        if (phantomRowRef.current != null) {
          // Phantom уже есть — просто встаём в нужную ячейку
          setSuggestSearchEnabled(false);
          setActiveCell({ rowId: PHANTOM_ROW_ID, columnKey: targetColumn.key });
          setEditor(targetColumn.getEditorFromRow(phantomRowRef.current));
          return;
        }

        // Создаём phantom row из defaults (без API-вызова)
        const phantom = { ...defaults, id: PHANTOM_ROW_ID } as unknown as Row;
        setPhantomRow(phantom);
        phantomRowRef.current = phantom;
        setRows(prev => [...prev, phantom]);
        setSuggestSearchEnabled(false);
        setActiveCell({ rowId: PHANTOM_ROW_ID, columnKey: targetColumn.key });
        setEditor(targetColumn.getEditorFromRow(phantom));
        return;
      }

      const column = columnMap.get(columnKey);
      if (!column) return;

      const row = getRowById(rowTarget);
      if (!row) return;

      setSuggestSearchEnabled(false);
      setActiveCell({ rowId: rowTarget, columnKey });
      setEditor(column.getEditorFromRow(row));
    },
    [columnMap, columns, defaults, getRowById]
  );

  const setEditorValue = useCallback(
    (nextValue: string) => {
      setSuggestSearchEnabled(Boolean(activeColumn?.suggest));
      if (!activeColumn) {
        setEditor({ value: nextValue });
        return;
      }

      setEditor(current => {
        if (activeColumn.onEditorValueChange) {
          return activeColumn.onEditorValueChange(current, nextValue);
        }
        return { ...current, value: nextValue };
      });
    },
    [activeColumn]
  );

  const selectSuggestion = useCallback(
    (option: EditableGridSuggestOption) => {
      const suggest = activeColumn?.suggest;
      if (!suggest) return;
      setSuggestSearchEnabled(false);
      setSuggestions([]);
      setSuggestLoading(false);
      setEditor(current => suggest.applyOption(option, current));
    },
    [activeColumn]
  );

  const moveToCell = useCallback(
    (rowId: number, columnKey: ColumnKey, sourceRow: Row | null) => {
      const nextColumn = columnMap.get(columnKey);
      if (!nextColumn) {
        resetEditor();
        return;
      }

      setSuggestSearchEnabled(false);
      setActiveCell({ rowId, columnKey });

      if (sourceRow) {
        setEditor(nextColumn.getEditorFromRow(sourceRow));
        return;
      }

      const row = getRowById(rowId);
      if (!row) {
        resetEditor();
        return;
      }

      setEditor(nextColumn.getEditorFromRow(row));
    },
    [columnMap, getRowById, resetEditor]
  );

  const commitActiveCell = useCallback(
    async (moveToNext: boolean = false) => {
      if (!activeCell || !activeColumn) return;

      const nextKey = moveToNext ? getNextColumn(activeCell.columnKey) : null;
      const row = getRowById(activeCell.rowId);
      if (!row) {
        resetEditor();
        return;
      }

      let nextEditor = editor;
      // Если у колонки задан placeholderValue (визуальная заглушка) и пользователь
      // оставил поле пустым — восстанавливаем placeholderValue, чтобы не нарушать
      // schema-ограничения вроде minLength=1.
      if (
        activeColumn.placeholderValue !== undefined &&
        nextEditor.value.trim() === ''
      ) {
        nextEditor = { ...nextEditor, value: activeColumn.placeholderValue };
      }
      const suggestConfig = activeColumn.suggest;
      const suggestCreate = suggestConfig?.create;
      const suggestBuildCreateContext = suggestConfig?.buildCreateContext;

      if (suggestConfig && suggestCreate && suggestBuildCreateContext) {
        const createContext = suggestBuildCreateContext(row, nextEditor);
        const shouldCreate = createContext.selectedRef == null && createContext.value.trim() !== '';

        if (shouldCreate) {
          setSaving(true);
          setRowSaving(activeCell.rowId, true);
          try {
            const createdRef = await suggestCreate(createContext);
            if (createdRef != null) {
              nextEditor = suggestConfig.applyOption(
                {
                  id: String(createdRef),
                  label: createContext.value,
                },
                nextEditor
              );
            }
          } catch (error) {
            handleError(error);
            return;
          } finally {
            setRowSaving(activeCell.rowId, false);
            setSaving(false);
          }
        }
      }

      const patch = activeColumn.buildRowPatch(row, nextEditor);
      const nextRow = { ...row, ...patch.apiPatch, ...(patch.localPatch ?? {}) } as Row;

      const computedPersistPatch = columns.reduce<EditableGridRowPatchResult<Row, ApiPatch>>(
        (acc, column) => {
          if (!column.persist) return acc;
          const nextValue = column.value
            ? column.value(nextRow)
            : (nextRow as Record<string, unknown>)[String(column.key)];
          const prevValue = (row as Record<string, unknown>)[String(column.key)];
          if (Object.is(nextValue, prevValue)) return acc;
          return {
            hasChanges: true,
            apiPatch: { ...acc.apiPatch, [column.key]: nextValue } as ApiPatch,
            localPatch: { ...(acc.localPatch ?? {}), [column.key]: nextValue } as Partial<Row>,
          };
        },
        { hasChanges: false, apiPatch: {} as ApiPatch, localPatch: {} as Partial<Row> }
      );

      const hasChanges = patch.hasChanges || computedPersistPatch.hasChanges;
      const apiPatch = { ...patch.apiPatch, ...computedPersistPatch.apiPatch };
      const localPatch = { ...patch.localPatch, ...computedPersistPatch.localPatch };

      // --- Phantom row: отложенное создание ---
      if (activeCell.rowId === PHANTOM_ROW_ID && phantomRowRef.current != null) {
        if (!hasChanges) {
          if (nextKey) {
            // Tab — переходим к следующей ячейке phantom-строки
            const updatedPhantom = { ...phantomRowRef.current, ...localPatch } as Row;
            moveToCell(PHANTOM_ROW_ID, nextKey, updatedPhantom);
          } else {
            // Blur без изменений — удаляем phantom
            cleanupPhantom();
            resetEditor();
          }
          return;
        }

        // Есть изменения — создаём реальную строку в БД
        setCreatingRow(true);
        creatingRowRef.current = true;
        try {
          const createPayload = { ...defaults, ...apiPatch } as CreatePayload;
          const created = await createRow(createPayload);
          const mapped = mapCreatedRow ? mapCreatedRow(created) : created;
          // Применяем localPatch к созданной строке (API может не вернуть computed-поля, напр. supplier_name)
          const mergedMapped = { ...mapped, ...(localPatch ?? {}) } as Row;
          const newRowId = getRowId(mergedMapped);
          if (newRowId == null) return;

          // Подменяем phantom на реальную строку
          setRows(prev => prev.map(r => (getRowId(r) === PHANTOM_ROW_ID ? mergedMapped : r)));
          setPhantomRow(null);
          phantomRowRef.current = null;

          if (nextKey) {
            moveToCell(newRowId, nextKey, mergedMapped);
          } else {
            resetEditor();
          }
        } catch (error) {
          handleError(error);
        } finally {
          creatingRowRef.current = false;
          setCreatingRow(false);
        }
        return;
      }

      // --- Обычная строка: update ---
      let effectiveRow = row;

      if (hasChanges) {
        setSaving(true);
        setRowSaving(activeCell.rowId, true);
        try {
          const updateResult = await updateRow(activeCell.rowId, apiPatch);
          const localPatchFromUpdate =
            updateResult && 'localPatch' in updateResult ? (updateResult.localPatch ?? {}) : {};
          const mergedLocalPatch = { ...(localPatch ?? {}), ...localPatchFromUpdate };

          if (Object.keys(mergedLocalPatch).length > 0) {
            effectiveRow = { ...row, ...mergedLocalPatch };
            setRows(prev =>
              prev.map(item => (getRowId(item) === activeCell.rowId ? effectiveRow : item))
            );
          }

          setJustSavedCell({ rowId: activeCell.rowId, columnKey: activeCell.columnKey });
        } catch (error) {
          handleError(error);
          return;
        } finally {
          setRowSaving(activeCell.rowId, false);
          setSaving(false);
        }
      }

      if (nextKey) {
        moveToCell(activeCell.rowId, nextKey, effectiveRow);
        return;
      }

      resetEditor();
    },
    [
      activeCell,
      activeColumn,
      cleanupPhantom,
      columns,
      createRow,
      defaults,
      editor,
      getNextColumn,
      getRowById,
      getRowId,
      handleError,
      mapCreatedRow,
      moveToCell,
      resetEditor,
      updateRow,
    ]
  );

  const deleteRowState = useCallback(
    async (rowId: number) => {
      // Phantom-строку просто удаляем локально, без API-вызова
      if (rowId === PHANTOM_ROW_ID) {
        cleanupPhantom();
        resetEditor();
        return;
      }

      setSaving(true);
      setRowSaving(rowId, true);
      try {
        await deleteRow(rowId);
        setRows(prev => prev.filter(row => getRowId(row) !== rowId));
        if (activeCell?.rowId === rowId) {
          resetEditor();
        }
      } catch (error) {
        handleError(error);
      } finally {
        setRowSaving(rowId, false);
        setSaving(false);
      }
    },
    [activeCell?.rowId, cleanupPhantom, deleteRow, getRowId, handleError, resetEditor, setRowSaving]
  );

  const replaceRows = useCallback((nextRows: Row[]) => {
    setRows(nextRows);
  }, []);

  /** ID phantom-строки (для передачи в view). null если phantom нет. */
  const phantomRowId = phantomRow != null ? PHANTOM_ROW_ID : null;

  /** Мгновенный toggle для checkbox-ячеек (минуя activeCell/editor). Optimistic update с откатом при ошибке. */
  const toggleCheckboxCell = useCallback(
    async (rowId: number, columnKey: ColumnKey) => {
      const column = columnMap.get(columnKey);
      const row = rows.find(r => getRowId(r) === rowId);
      if (!column || !row) return;

      const patch = column.buildRowPatch(row, { value: '' });
      if (!patch.hasChanges) return;

      const optimisticPatch = { ...patch.apiPatch, ...(patch.localPatch ?? {}) };
      setRows(prev => prev.map(r => (getRowId(r) === rowId ? { ...r, ...optimisticPatch } : r)));

      setRowSaving(rowId, true);
      try {
        const result = await updateRow(rowId, patch.apiPatch);
        const localPatchFromUpdate =
          result && 'localPatch' in result ? (result.localPatch ?? {}) : {};
        if (Object.keys(localPatchFromUpdate).length > 0) {
          setRows(prev =>
            prev.map(r => (getRowId(r) === rowId ? { ...r, ...localPatchFromUpdate } : r))
          );
        }
      } catch (error) {
        // Откат к предыдущему состоянию
        setRows(prev => prev.map(r => (getRowId(r) === rowId ? { ...r, ...row } : r)));
        handleError(error);
      } finally {
        setRowSaving(rowId, false);
      }
    },
    [columnMap, rows, getRowId, updateRow, handleError, setRowSaving]
  );

  const gridProps = useMemo(
    () => ({
      columns,
      rows,
      getRowId,
      loading,
      activeCell,
      activeColumn,
      editor,
      suggestions,
      creatingRow,
      phantomRowId,
      justSavedCell,
      onStartCellEdit: startCellEdit,
      onCancelActiveCell: cancelActiveCellWithCleanup,
      onCommitActiveCell: commitActiveCell,
      onSetEditorValue: setEditorValue,
      onSelectSuggestion: selectSuggestion,
      onToggleCheckboxCell: toggleCheckboxCell,
    }),
    [
      columns,
      rows,
      getRowId,
      loading,
      activeCell,
      activeColumn,
      editor,
      suggestions,
      creatingRow,
      phantomRowId,
      justSavedCell,
      startCellEdit,
      cancelActiveCellWithCleanup,
      commitActiveCell,
      setEditorValue,
      selectSuggestion,
      toggleCheckboxCell,
    ]
  );

  return {
    gridProps,
    columns,
    getRowId,

    rows,
    loading,
    saving,
    isRowSaving,
    creatingRow,

    activeCell,
    activeColumn,
    editor,

    suggestions,
    suggestLoading,

    loadRows: loadRowsState,
    deleteRow: deleteRowState,
    replaceRows,

    startCellEdit,
    setEditorValue,
    setEditor,
    selectSuggestion,
    commitActiveCell,
    cancelActiveCell: cancelActiveCellWithCleanup,
    toggleCheckboxCell,
  };
}
